import React, { Fragment, useEffect, useRef, useState } from 'react';
import CardProduct from './CardProduct';
import { useLayoutContext } from '../../../Contexts/LayoutContext';
import CardProductGrid from './CardProductGrid';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import NavigateToCategoryArrows from '../NavigateToCategoryArrows';
import NotifySwiperCategory from '../NotifySwiperCategory';
import MenuCategories from '../MenuCategories';
import { VIEW_MODE_GRID, VIEW_MODE_IMAGE_GALLERY, VIEW_MODE_LIST } from '../../../Utils/Constants/Product';
import BoxGallerySwiperImageFixed from './BoxGallerySwiperImageFixed';
import TitleSubcategory from './TitleSubcategory';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { useLoginContext } from '../../../Contexts/LoginContext';
import { useCartContext } from '../../../Contexts/CartContext';

interface ProductsProps {
    categories: any;
    loading: boolean;
}

const Products = ({
    categories = [],
    loading
}: ProductsProps) => {

    const { viewMode, isCategoriesSwiperViewed, setIsCategoriesSwiperViewed, setShowSearchBar, isCartInfoViewed, currentSwiperSlide, setCurrentSwiperSlide, isMobile, enableLoadProducts, welcomeScreenRead, mainLoading, loadingCategories } = useLayoutContext();
    const { showLoginBasic, showLoginStoreCounter } = useLoginContext();
    const { isStoreCounter } = useOrderTypeContext();
    const { showConfirmTable } = useCartContext();
    const swiperRefProducts = useRef<SwiperRef>(null);
    const [isVisibleSwipeInfo] = useState(true);
    const [currentCategory, setCurrentCategory] = useState<any>(null);
    const [isOnlySelectedMenu, setIsOnlySelectedMenu] = useState<boolean>(false);
    const [isShowNotifySwiperCategory, setIsShowNotifySwiperCategory] = useState<boolean>(false);
    const selectedItemRef = useRef<HTMLDivElement | null>(null);
    const [activeCategory, setActiveCategory] = useState<number | null>(null);
    const [heightSpaceHeader, setHeightSpaceHeader] = useState<number>(0);

    const handleCurrentSlide = (index: number, category?:any) => {
        setShowSearchBar(false);
        setCurrentSwiperSlide(index);
        setCurrentCategory(category);

        if (swiperRefProducts.current && swiperRefProducts.current.swiper) {
            swiperRefProducts.current.swiper.slideTo(index, !enableLoadProducts ? 0 : 300);
        }
        // centralizando menu de categorias
        if (selectedItemRef.current) {
            selectedItemRef.current.scrollIntoView({
                behavior: !enableLoadProducts ? 'auto' : 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
        if (!isOnlySelectedMenu) {
            handlePositionContent(category);
        }
    }

    const handlePositionContent = (category?: any) => {

        if ((!category && !category) || isOnlySelectedMenu) {
            return;
        }
        setIsOnlySelectedMenu(false);

        let topContent = 0;
        let topContentMobile = 0;
        let spaceHeader = 0;

        const boxHeader = document.getElementById("boxHeader");
        if (boxHeader) {
            const heightHeader = boxHeader.getBoundingClientRect().height;
            topContent += heightHeader;
            spaceHeader += heightHeader; // Altura mínima do box do swiper ocupará a tela inteira menos o header
        }

        const boxBanners = document.getElementById("boxBanners");
        if (boxBanners) {
            let gapHeight = (4 * 8); // valor base de gap-8 (0.25rem : 4px)
            const heightBanners = boxBanners.getBoundingClientRect().height;
            topContentMobile += heightBanners + gapHeight;
        }

        const boxMenuCategory = document.getElementById('boxMenuCategory');
        if (boxMenuCategory) {
            const heightMenuCategory = boxMenuCategory.getBoundingClientRect().height;
            topContent += heightMenuCategory;
            spaceHeader += heightMenuCategory;
        }

        const boxProductsContent = document.getElementById('boxProductsContent');
        if (boxProductsContent) {
            const rect = boxProductsContent.getBoundingClientRect();
            topContent += rect.top + window.scrollY;
        }

        if (category && viewMode === VIEW_MODE_LIST) {
            setIsOnlySelectedMenu(false);

            // Trecho para corrigir um comportamento nativo do scroll em mobile, especialmente no Chrome Android
            // No desktop o ScrollSpy faz a navegação direta pelo href="#..."
            if (isMobile) {
                setTimeout(() => {
                    const categoryId = category.id ?? 'featured';
                    const positionCategory = document.getElementById(`category_${categoryId}`);
                    if (positionCategory) {
                        const rect = positionCategory.getBoundingClientRect();
                        const y = rect.top + window.scrollY - topContent;
                        window.scrollTo({
                            top: y,
                            behavior: 'smooth'
                        });
                        return;
                    }
                }, 300);
            }

        } else {
            // Alinhando ao topo na troca de categorias/slide
            //* Categoria sem id (destaques)
            //* Quanto não for destaque rolar até ocultar os banners
            if (!category.id) {
                topContentMobile = 0;
            }

            setHeightSpaceHeader(Math.ceil(isMobile ? spaceHeader : topContent));
            window.scrollTo({
                top: isMobile ? topContentMobile : topContent,
                behavior: 'smooth'
            });
        }
    }

    useEffect(() => {
        handleCurrentSlide(currentSwiperSlide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setTimeout(() => {
            setIsShowNotifySwiperCategory(!isCategoriesSwiperViewed && isVisibleSwipeInfo && !loading && (!isStoreCounter || (isStoreCounter && welcomeScreenRead && !showLoginBasic && !showLoginStoreCounter && !mainLoading && !showConfirmTable)));
        }, 300);
    }, [isCategoriesSwiperViewed, isStoreCounter, isVisibleSwipeInfo, loading, showLoginBasic, showLoginStoreCounter, welcomeScreenRead, mainLoading, showConfirmTable]);

    useEffect(() => {
        if (!loadingCategories) {
            handleCurrentSlide(currentSwiperSlide);
            if (viewMode !== VIEW_MODE_LIST) {
                handlePositionContent(categories[currentSwiperSlide]);
            }
            setIsOnlySelectedMenu(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSwiperSlide, viewMode, loading, loadingCategories])

    useEffect(() => {
        if (viewMode === VIEW_MODE_LIST && !loadingCategories) {
            handlePositionContent(currentCategory);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCategory, loadingCategories])

    const handleCategoriesSwiperViewed = () => {
        setIsCategoriesSwiperViewed(true);
    }

    const getClassNameByViewMode = (view: string) => {
        const classes: Record<string, string> = {
            list: 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 lg:gap-6',
            grid: 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-2 md:gap-3 lg:gap-4 xl:gap-5',
            swiper_image: 'grid-cols-2 items-start',
        };
        return classes[view] || 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7';
    };

    const getClassNameSubtitleByViewMode = (view: string) => {
        const classes: Record<string, string> = {
            list: 'col-span-1 md:col-span-2 xl:col-span-3',
            grid: 'col-span-3 sm:col-span-4 md:col-span-5 lg:col-span-6 xl:col-span-7',
            swiper_image: 'col-span-2',
        };
        return classes[view] || 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7';
    };

    const renderProduct = (product: any, index: number, category: any, indexFixed?: number) => {
        const mode = category.layout || viewMode;
        if (mode === VIEW_MODE_IMAGE_GALLERY) {
            return <BoxGallerySwiperImageFixed
                        product={product}
                        index={index}
                        indexFixed={indexFixed}
                        category={category}
                        loading={loading}
                        key={`gallery_${index}`}
                    />
        } else if (mode === "list") {
            return <CardProduct
                        product={product}
                        category={category}
                        loading={loading}
                        key={`list_${index}`}
                    />
        } else if (mode === "grid") {
            return <CardProductGrid
                        product={product}
                        category={category}
                        loading={loading}
                        key={`grid_${index}`}
                    />;
        }

        return null;
    };

    const renderProductsImageGallery = (category: any) => {
        const groups = category.products.reduce(
            (acc: any[][], product: any, index: number) => {
                const columnIndex = index % 2; //* Pares e ímpares para ordenação

                acc[columnIndex].push(
                    renderProduct(product, product.id, category, index)
                );

                return acc;
            },
            [[], []] //* duas colunas fixas
        );

        return groups.map((group: any[], groupIndex: number) => (
            <div key={groupIndex} className="grid gap-2 content-start">
                {group}
            </div>
        ));
    };

    useEffect(() => {
        let scrollTimeout: any = null;

        const handleScrollEnd = () => {
            const items = document.querySelectorAll<HTMLElement>(".categoryListScroll > h2");
            let currentMenuCategory: number | null = null;

            items.forEach((item, index) => {
                const rect = item.getBoundingClientRect();

                if (rect.top < 160) {
                    currentMenuCategory = index;
                }
            });

            if (currentMenuCategory !== null && currentMenuCategory !== activeCategory) {
                setActiveCategory(currentMenuCategory);
                handleCurrentSlide(currentMenuCategory, categories[currentMenuCategory]);
                setCurrentCategory(categories[currentMenuCategory]);
                setIsOnlySelectedMenu(true);
            }
        };

        const handleScroll = () => {
            // sempre reinicia o timer
            clearTimeout(scrollTimeout);

            scrollTimeout = setTimeout(() => {
                handleScrollEnd(); // só roda quando o scroll PARAR
            }, 120);
        };

        window.addEventListener("scroll", handleScroll, { passive: true });

        // executa uma vez ao carregar
        handleScrollEnd();

        return () => {
            window.removeEventListener("scroll", handleScroll);
            clearTimeout(scrollTimeout);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeCategory]);

    return (
        <section className={`flex flex-col md:min-h-min`} style={{ minHeight: `calc(100dvh - ${heightSpaceHeader}px)` }}>

            <NotifySwiperCategory
                show={isShowNotifySwiperCategory}
                onClickConfirm={handleCategoriesSwiperViewed}
                classNameIcon='text-white'
            />

            { /* MENU CATEGORIES */ }
            {(viewMode === VIEW_MODE_GRID || viewMode === VIEW_MODE_IMAGE_GALLERY) &&
                <div id='boxProductsContent' className='relative grid auto-rows-auto items-start self-start'>

                    { /* MENU CATEGORIES */ }
                    <MenuCategories
                        categories={categories}
                        currentSwiperSlide={currentSwiperSlide}
                        selectedItemRef={selectedItemRef}
                        onHandleClick={(e: number, cat: any) => handleCurrentSlide(e, cat)}
                        loading={loading}
                    />

                    <Swiper
                        ref={swiperRefProducts}
                        spaceBetween={0}
                        centeredSlides={true}
                        slideToClickedSlide={true}
                        slidesPerView={'auto'}
                        autoHeight={true}
                        onSlideChange={(e: any) => { handleCurrentSlide(e.activeIndex); handleCategoriesSwiperViewed(); }}
                        id='boxProductsSwiper'
                        className={`w-full ${!isCategoriesSwiperViewed || isCartInfoViewed === false ? 'pointer-events-none' : ''}`}
                    >
                        {categories?.map((category: any, index: number) =>
                            <SwiperSlide
                                id={`category_${category.id ?? 'featured'}`}
                                key={`${index}_${category.id ?? 'featured'}`}
                                data-hash={`slide${category.id ?? 'featured'}`}
                                data-active={currentSwiperSlide === index}
                                className={`swiperSlideProducts w-full min-w-full cursor-pointer p-1 cursor-grab active:cursor-grabbing
                                    h-full ${isMobile ? 'min-h-[65dvh]' : 'min-h-dvh'}
                                `}>

                                {category.description && <div className='text-sm text-center text-gray-400 italic pb-3 leading-4'>{ category.description }</div>}

                                <section className={`
                                    grid w-full gap-2 md:gap-3 lg:gap-4 xl:gap-5 transition-width ease-in-out duration-200
                                    ${ getClassNameByViewMode(category.layout ?? viewMode)}
                                `}>

                                    {/* Render de produtos FIXO do swiper_image */}
                                    {(category.layout === VIEW_MODE_IMAGE_GALLERY || (viewMode === VIEW_MODE_IMAGE_GALLERY && !category.layout))
                                        ? renderProductsImageGallery(category)
                                        : (
                                            /* Render dinâmico de produtos com base no layout ou viewMode */
                                            category.products.map((product: any, index: number) => (
                                                renderProduct(product, product.id, category, index)
                                            ))
                                        )
                                    }

                                    {category.children?.length > 0 &&
                                        category.children.map((child: any) => (
                                            <Fragment key={child.id}>
                                                <TitleSubcategory category={child} className={`mt-4 mb-2 ${getClassNameSubtitleByViewMode(category.layout ?? viewMode)}`} />

                                                {child.products?.length > 0 &&
                                                    child.products.map((product: any) =>
                                                      renderProduct(product, product.id, child)
                                                )}
                                            </Fragment>
                                        ))
                                    }

                                    <NavigateToCategoryArrows
                                        show={!loading}
                                        index={index}
                                        categoryPrevName={index > 0 ? categories[index - 1].name : null}
                                        categoryNextName={index < categories.length - 1 ? categories[index + 1].name : null}
                                        showPrev={index > 0}
                                        showNext={index < categories.length - 1}
                                        onClickCategory={(e: number) => handleCurrentSlide(e)}
                                    />
                                </section>
                            </SwiperSlide>
                        )}
                    </Swiper>
                </div>
            }

            {(viewMode === VIEW_MODE_LIST) &&
                <>
                    <MenuCategories
                        categories={categories}
                        currentSwiperSlide={currentSwiperSlide}
                        selectedItemRef={selectedItemRef}
                        onHandleClick={(e: number, category: any) => handleCurrentSlide(e, category)}
                        loading={loading}
                    />
                    <div id='boxProducts' className={`grid grid-rows-[auto,1fr] h-full p-1 gap-12
                        ${isMobile && isCartInfoViewed === false && !loading && 'opacity-10'}
                        `}>
                        {categories?.map((category: any, index: number) =>

                            <Fragment key={`${category.id}_${index}_list`}>
                                <div className='categoryListScroll' id={`_${index}`}>

                                    <h2 id={`category_${category.id ?? 'featured'}`} className={`grid gap-0.5 mb-3`}>
                                        <span className='text-xl font-extrabold text-shopfood-title'>{category.name}</span>
                                        {category.description && <div className='text-sm md:text-base text-gray-400 italic pb-3 leading-4'>{ category.description }</div>}
                                    </h2>

                                    <section className={`grid transition-width
                                        ${category.layout && isMobile
                                            ? getClassNameByViewMode(category.layout)
                                            : 'md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 lg:gap-6'}
                                    `}>

                                        {/* Render de produtos FIXO do swiper_image */}
                                        {(category.layout === VIEW_MODE_IMAGE_GALLERY)
                                            ? renderProductsImageGallery(category)
                                            : (
                                                /* Render dinâmico de produtos com base no layout ou viewMode */
                                                category.products.map((product: any, index: number) => (
                                                    renderProduct(product, product.id, category, index)
                                                ))
                                            )
                                        }


                                    </section>

                                </div>

                                {category.children?.length > 0 &&
                                    category.children.map((child: any) => (
                                        <div key={child.id}>
                                            <TitleSubcategory category={child} className={`mb-4 ${getClassNameSubtitleByViewMode(category.layout ?? viewMode)}`} />

                                            <div className={`
                                                grid transition-width
                                                pl-2
                                                ${child.children.layout && isMobile
                                                    ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-3 lg:gap-4 xl:gap-5'
                                                    : 'md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4 lg:gap-6'
                                            }`}>
                                                {child.products?.length > 0 &&
                                                    child.products.map((product: any, index: number) =>
                                                        renderProduct(product, product.id, child, index)
                                                )}
                                            </div>
                                        </div>
                                    ))
                                }
                            </Fragment>
                        )}
                    </div>
                </>
            }
        </section>
    )
}

export default Products;
